import React, { useContext, useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { AppContext } from 'src/store/ContextStore';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';
import { CONSTANTS } from 'src/components/Constants';
import { CLINICAL_COLOURS } from 'src/theme/palette';
import { RankCard, AiSubsectionHeader, Empty } from './clinicalInsightsUi';
import {
  CASCADE_BADGE_COLORS,
  getCascadeBadgeTier,
  cascadeBadgeText
} from '../../pedigree-tool/analysis/cascadeBadge';
import {
  patternLabel,
  ROLE_LABEL
} from '../../pedigree-tool/analysis/constants';

// Renders the (pure, client-side) pedigree-analysis result in the Clinical
// Insights sidebar. Family view (no node selected): inheritance pattern,
// family-aware syndromes, cascade testing, review alerts. Individual view:
// the selected member's role, tags and recommended actions.

// TGCLOUD-1669 — these two maps moved to ../../pedigree-tool/analysis/constants
// so the panel, the cascade prior wording and the collapsed-section summary all
// read one source. Three copies is how the summary ended up printing the raw id.
// The role is the single highest-precedence classification; tags list every
// attribute that applies. Each role (bar unaffected/unknown) maps to one tag —
// we drop that tag so the role isn't rendered twice as a chip.
const ROLE_TO_TAG = {
  proband: 'proband',
  affected: 'affected',
  obligate_carrier: 'obligate carrier',
  tested_carrier: 'tested carrier',
  reported_carrier: 'reported carrier',
  carrier: 'carrier',
  cascade_candidate: 'cascade candidate'
};
const CONF_COLOR = {
  strong: CLINICAL_COLOURS.matchStrong,
  moderate: CLINICAL_COLOURS.matchModerate,
  weak: CLINICAL_COLOURS.matchWeak,
  ruled_out: CLINICAL_COLOURS.matchRuledOut
};

// The engine's confidence code ('strong', 'ruled_out'), shown as its words.
const ConfidenceChip = ({ confidence }) => {
  const t = useAutoTranslation();
  return confidence ? (
    <Chip
      label={t(confidence.replace('_', ' '))}
      size="small"
      sx={{
        height: 18,
        fontSize: '.6rem',
        textTransform: 'capitalize',
        bgcolor: CONF_COLOR[confidence] || '#9e9e9e',
        color: '#fff'
      }}
    />
  ) : null;
};

const UpnChip = ({ upn }) => {
  const t = useAutoTranslation();
  return (
    <Chip
      label={t('UPN {{upn}}', { upn })}
      size="small"
      variant="outlined"
      sx={{ height: 18, fontSize: '.6rem' }}
    />
  );
};

// "Father's Father" → "Father's father" (leading capital, rest lowercase).
const toSentenceCase = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

// Last-resort prettifier for an analyzer token (e.g. "IND_3") when the live
// member record can't be found. Real relationships resolve via CONSTANTS. The
// two words are translated; a token is an id, shown as it is.
const humanizeToken = (token) => {
  if (!token) return translate('Relative');
  if (token === 'PROBAND') return translate('Proband');
  return toSentenceCase(token.replace(/_/g, ' '));
};

// Readable relationship for a cascade tile. Prefer the live member's
// `relationship` key (mapped via CONSTANTS.relations, the app's own fixed
// names, so translated); fall back to the token.
const relationshipLabel = (member, token) => {
  if (member?.proBandId) return translate('Proband');
  const label =
    member?.relationship && CONSTANTS.relations[member.relationship];
  return label ? translate(toSentenceCase(label)) : humanizeToken(token);
};

// Proband is conventionally UPN 1 (mirrors formatPersonSummary in the sidebar).
const upnOf = (member) =>
  member ? (member.proBandId ? 1 : member.upn ?? null) : null;

// DR-2: the inheritance panel no longer shows a bare confidence word. The chip
// carries the OBSERVATION and the card body carries its limitation, so the
// claim can never be read without what it rests on. "Strong" was reachable on
// two affected relatives for every pattern, which is what made it misleading.
const EvidenceChip = ({ evidence, t }) => {
  const statement = evidence?.statement;
  if (!statement) return null;
  return (
    <Chip
      label={t(statement)}
      size="small"
      variant="outlined"
      sx={{ height: 18, fontSize: '.6rem', maxWidth: 190 }}
    />
  );
};

// Familial aggregation: relatives share a diagnosis and nothing more is
// claimed. Rendered as its OWN group, deliberately not mixed in with
// inheritance patterns — a reader must not mistake it for a weaker pattern.
const AggregationCard = ({ agg, diseaseName, t }) => (
  <RankCard
    accent="#8d9db6"
    title={diseaseName(agg.diseaseId)}
    meta={t('Familial aggregation — no mode of inheritance stated')}
    supportingMemberIds={agg.supportingIds}
    right={<EvidenceChip evidence={agg.evidence} t={t} />}
    lines={(agg.rationale || []).map((r) => t(r))}
  />
);

// What this relative IS, never what to do about them (DR-6).
//
// `cascadeBadgeText` returns null for an unpriced candidate, which is why the
// old `Math.round(prior * 100)` call sites had to go: with the mode-aware
// priors in place a null prior rendered as "NaN%".
function cascadeTitle(candidate, t) {
  if (candidate?.derivation?.kind === 'deduction') return t('Obligate carrier');
  const pct = cascadeBadgeText(candidate);
  if (!pct) return t('No prior available');
  return pct;
}

// The derivation, then every qualifier the engine attached. Qualifiers are
// rendered from the array rather than from individual booleans, so a surface
// cannot silently show a bare number by forgetting one.
function cascadeLines(candidate, t) {
  const lines = [];
  if (candidate?.derivation?.summary)
    lines.push(t(candidate.derivation.summary));
  if (candidate?.priorUnavailable?.summary)
    lines.push(t(candidate.priorUnavailable.summary));
  for (const q of candidate?.qualifiers || []) {
    if (q?.summary) lines.push(t(q.summary));
  }
  if (candidate?.knownResult?.result) {
    lines.push(
      `${t('Result on file')}: ${t(candidate.knownResult.result)}${
        candidate.knownResult.gene ? ` · ${candidate.knownResult.gene}` : ''
      }`
    );
  }
  return lines;
}

export default function PedigreeMatchMenu({ analysis, data, memberRecords }) {
  const t = useAutoTranslation();
  const { content } = useContext(AppContext);

  const diseaseName = useMemo(() => {
    const map = {};
    for (const d of content?.pedigreeSelectedUsers?.diagnoses || [])
      map[d.id] = d.name;
    return (id) => map[id] || id;
  }, [content]);

  // pid → live member record, for resolving UPN + relationship on tiles.
  const memberByPid = useMemo(() => {
    const map = {};
    for (const m of memberRecords || []) map[String(m.pid)] = m;
    return map;
  }, [memberRecords]);

  if (!analysis) return <Empty text={t('No pedigree analysis available.')} />;

  // ---- Individual view ----
  if (data) {
    const section = analysis.individuals?.[String(data.pid)];
    if (!section) return <Empty text={t('No analysis for this individual.')} />;
    const patternLabels = (section.referencedPatterns || [])
      .map((id) => t(patternLabel(id)))
      .join(', ');
    // Drop the tag the role already represents (e.g. role "Affected" + tag
    // "affected"); keep only the additional attributes.
    const extraTags = (section.tags || []).filter(
      (tag) => tag !== ROLE_TO_TAG[section.role]
    );
    return (
      <Box sx={{ p: 0.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          <Chip
            label={t(ROLE_LABEL[section.role] || section.role)}
            size="small"
            color="primary"
            sx={{ height: 20, fontSize: '.65rem' }}
          />
          {extraTags.map((tag) => (
            <Chip
              key={tag}
              label={t(tag)}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '.65rem' }}
            />
          ))}
        </Box>
        {patternLabels && (
          <Typography
            sx={{ fontSize: '.75rem', color: 'text.secondary', mb: 1 }}
          >
            {t('Contributes to family pattern')}: {patternLabels}
          </Typography>
        )}
        {section.individualActions?.length > 0 && (
          <>
            {/* Information, not instructions (DR-6). The title is what this
                person IS in relation to the family, and the body is the
                derivation of the number — never an imperative. */}
            <AiSubsectionHeader>{t('Cascade information')}</AiSubsectionHeader>
            {section.individualActions.map((a, i) => (
              <RankCard
                key={i}
                accent={CASCADE_BADGE_COLORS[getCascadeBadgeTier(a)]}
                title={cascadeTitle(a, t)}
                meta={a.variant?.gene || null}
                lines={cascadeLines(a, t)}
              />
            ))}
          </>
        )}
      </Box>
    );
  }

  // ---- Family view ----
  const fam = analysis.family || {};
  const patterns = fam.inheritancePatterns || [];
  const syndromes = fam.syndromes || [];
  const cascade = fam.cascadeCandidates || [];
  const alerts = fam.alerts || [];
  const aggregations = fam.familialAggregations || [];
  const hasAny =
    patterns.length ||
    aggregations.length ||
    syndromes.length ||
    cascade.length ||
    alerts.length;

  // Group cascade candidates by family member: one tile per person listing
  // every disease they're a candidate for. Tiles ordered by the member's
  // highest prior (so first-degree / highest-yield people surface first);
  // diseases within a tile ordered by prior too.
  const cascadeGroups = (() => {
    const groups = new Map();
    for (const c of cascade) {
      if (!groups.has(c.pid))
        groups.set(c.pid, { pid: c.pid, token: c.token, items: [] });
      groups.get(c.pid).items.push(c);
    }
    return [...groups.values()]
      .map((g) => {
        const items = [...g.items].sort(
          (a, b) => (b.priorProbability || 0) - (a.priorProbability || 0)
        );
        return {
          ...g,
          items,
          topPrior: items[0]?.priorProbability || 0,
          // Read the deferral object, not the legacy action: the action
          // field only defers a minor with a RECORDED age, so a minor with no
          // date of birth — the case W4.8 closed — would slip past it.
          allDefer: items.every((i) => i.deferral?.deferred)
        };
      })
      .sort((a, b) => b.topPrior - a.topPrior);
  })();

  if (!hasAny)
    return (
      <Empty
        text={t(
          'No pedigree-level findings yet. Add affected relatives, diagnoses and carrier status to generate insights.'
        )}
      />
    );

  return (
    <Box sx={{ p: 0.5 }}>
      {patterns.length > 0 && (
        <>
          <AiSubsectionHeader>{t('Inheritance pattern')}</AiSubsectionHeader>
          {patterns.map((p, i) => (
            <RankCard
              key={i}
              accent="#2ba9bf"
              title={t(patternLabel(p.id))}
              meta={diseaseName(p.diseaseId)}
              supportingMemberIds={p.supportingIds}
              // DR-2: no bare confidence word. The evidence statement says what
              // was observed and the limitation says what it does not
              // establish, so the reader cannot see the claim without its
              // limits.
              right={<EvidenceChip evidence={p.evidence} t={t} />}
              // Full rationale, ordered most-important-first. Was
              // `.slice(0, 1)`, which threw away every caveat the engine
              // computed — including "affected female(s) present, less
              // typical" on the very patterns that caveat qualifies.
              lines={(p.rationale || []).map((r) => t(r))}
            />
          ))}
        </>
      )}

      {aggregations.length > 0 && (
        <>
          <AiSubsectionHeader>{t('Familial aggregation')}</AiSubsectionHeader>
          {aggregations.map((agg, i) => (
            <AggregationCard
              key={i}
              agg={agg}
              diseaseName={diseaseName}
              t={t}
            />
          ))}
        </>
      )}

      {syndromes.length > 0 && (
        <>
          <AiSubsectionHeader>
            {t('Syndromes (family-aware)')}
          </AiSubsectionHeader>
          {syndromes.map((s, i) => (
            <RankCard
              key={i}
              accent="#7b1fa2"
              title={t(s.label)}
              meta={s.omim ? `OMIM:${s.omim}` : null}
              supportingMemberIds={s.supportingIds}
              right={<ConfidenceChip confidence={s.confidence} />}
              lines={(s.rationale || []).map((r) => t(r))}
            />
          ))}
        </>
      )}

      {cascadeGroups.length > 0 && (
        <>
          <AiSubsectionHeader>{t('Cascade testing')}</AiSubsectionHeader>
          {cascadeGroups.map((g) => {
            const member = memberByPid[String(g.pid)];
            const upn = upnOf(member);
            return (
              <RankCard
                key={g.pid}
                accent={
                  CASCADE_BADGE_COLORS[
                    getCascadeBadgeTier({
                      priorProbability: g.topPrior,
                      deferral: g.allDefer ? { deferred: true } : null
                    })
                  ]
                }
                title={relationshipLabel(member, g.token)}
                supportingMemberIds={[g.pid]}
                right={upn != null ? <UpnChip upn={upn} /> : null}
                lines={g.items.flatMap((c) => {
                  const dz = c.diseaseId
                    ? diseaseName(c.diseaseId)
                    : t('Disease');
                  return [
                    `${dz} — ${cascadeTitle(c, t)}`,
                    ...cascadeLines(c, t)
                  ];
                })}
              />
            );
          })}
        </>
      )}

      {alerts.length > 0 && (
        <>
          <AiSubsectionHeader>{t('Review needed')}</AiSubsectionHeader>
          {alerts.map((a, i) => (
            <RankCard
              key={i}
              accent={
                a.severity === 'review'
                  ? CLINICAL_COLOURS.matchReviewAlert
                  : CLINICAL_COLOURS.matchInfoAlert
              }
              title={t(a.detail)}
              right={
                <Chip
                  label={t(a.severity)}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '.6rem',
                    textTransform: 'capitalize'
                  }}
                />
              }
            />
          ))}
        </>
      )}

      {/* Provenance of the analysis itself. The ruleset version and generation
          time were already computed into analysis.meta but displayed nowhere,
          so a clinician could not tell which version of the rules produced a
          conclusion, and an output could not be reconstructed after the rules
          changed. */}
      {analysis.meta?.rulesetVersion && (
        <Typography
          sx={{
            mt: 1.5,
            fontSize: 10.5,
            opacity: 0.6,
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {t('Analysis ruleset')} {analysis.meta.rulesetVersion}
          {analysis.meta.generatedAt
            ? ` · ${new Date(analysis.meta.generatedAt).toLocaleString()}`
            : ''}
        </Typography>
      )}
    </Box>
  );
}
